

import React, { useState, useEffect } from 'react';
import { UserData, DiscountCode } from '../types';
import LegalModal from './LegalModal';
import { supabase } from '../services/supabaseClient';


interface UserProfileModalProps {
  user: UserData;
  onClose: () => void;
  onLogout: () => void;
  onUpdateUser: (data: Partial<UserData>) => void;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({ user, onClose, onLogout, onUpdateUser }) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'support' | 'settings'>('profile');
  const [showSuccess, setShowSuccess] = useState<string | null>(null);
  const [latency, setLatency] = useState<number>(0);
  
  // Settings state
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [settingsError, setSettingsError] = useState<string | null>(null);
  
  // Support form state
  const [supportSubject, setSupportSubject] = useState('');
  const [supportMessage, setSupportMessage] = useState('');
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  
  const [editedUser, setEditedUser] = useState({
    name: user.name,
    email: user.email,
    bar: user.bar || ''
  });
  const [isSaving, setIsSaving] = useState(false);

  // Discount logic
  const [voucherInput, setVoucherInput] = useState('');
  const [activeDiscount, setActiveDiscount] = useState<DiscountCode | null>(null);
  const [voucherError, setVoucherError] = useState<string | null>(null);

  useEffect(() => {
    const start = performance.now();
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    if (!supabaseUrl) return;
    fetch(supabaseUrl, { mode: 'no-cors' }).then(() => setLatency(Math.round(performance.now() - start)));
  }, []);

  const handleApplyVoucher = () => {
    setVoucherError(null);
    const savedVouchers: DiscountCode[] = JSON.parse(localStorage.getItem('lexsmart_vouchers') || '[]');
    const found = savedVouchers.find(v => v.code === voucherInput.toUpperCase().trim() && v.isActive);

    if (found) {
      setActiveDiscount(found);
      setShowSuccess(`Cod aplicat: -${found.value}${found.type === 'percentage' ? '%' : '€'}`);
      setTimeout(() => setShowSuccess(null), 3000);
    } else {
      setVoucherError("Codul introdus este invalid sau expirat.");
      setActiveDiscount(null);
    }
  };

  const calculateDiscountedPrice = (base: number) => {
    if (!activeDiscount) return base;
    if (activeDiscount.type === 'percentage') {
      return base - (base * (activeDiscount.value / 100));
    }
    return Math.max(0, base - activeDiscount.value);
  };

  const handleProfileSave = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setTimeout(() => {
      onUpdateUser(editedUser);
      setIsSaving(false);
      setShowSuccess("Profil actualizat!");
      setTimeout(() => setShowSuccess(null), 3000);
    }, 800);
  };

  const handleSupportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSupportSubmitting(true);
    setSettingsError(null);

    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      const { error } = await supabase.from('support_messages').insert([{
        user_id: authUser?.id,
        user_email: user.email,
        user_name: user.name,
        subject: supportSubject,
        message: supportMessage,
        status: 'new'
      }]);

      if (error) throw error;

      setShowSuccess('Mesaj trimis cu succes! Vei primi răspuns în maximum 24h.');
      setSupportSubject('');
      setSupportMessage('');
      setTimeout(() => setShowSuccess(null), 5000);
    } catch (err: any) {
      setSettingsError(err.message || 'Eroare la trimiterea mesajului.');
    } finally {
      setSupportSubmitting(false);
    }
  };

  const userInitials = user.name.split(' ').filter(n => n !== 'Av.').map(n => n[0]).join('').toUpperCase();

  const tabs = [
    { id: 'profile', label: 'PROFIL', icon: '👤' },
    { id: 'settings', label: 'SETĂRI', icon: '🔐' },
    { id: 'support', label: 'SUPORT', icon: '🆘' },
  ];

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[200] flex items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full max-w-4xl h-[100dvh] sm:h-[85vh] sm:rounded-[4rem] shadow-2xl flex flex-col md:flex-row overflow-hidden animate-in zoom-in-95 duration-300 border border-white/20">
        
        <div className="w-full md:w-72 bg-slate-50 border-r border-slate-100 p-6 md:p-10 flex flex-col shrink-0">
          <div className="mb-8 md:mb-12 flex items-center gap-5">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-indigo-600 flex items-center justify-center text-white font-black text-xl shadow-2xl shadow-indigo-100 shrink-0 italic">
              {userInitials}
            </div>
            <div className="min-w-0">
              <h3 className="font-black text-slate-900 truncate text-sm italic leading-none">{user.name}</h3>
              <p className="text-[9px] text-indigo-500 font-black uppercase tracking-[0.2em] mt-1.5">{user.isApproved ? 'MEMBRU BETA' : 'ÎN AȘTEPTARE'}</p>
            </div>
          </div>

          <div className="bg-slate-200/50 p-1 rounded-2xl md:rounded-[1.75rem] flex md:flex-col gap-1 overflow-x-auto hide-scrollbar shrink-0 mb-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-grow md:flex-none text-center md:text-left px-4 py-3 md:py-4 rounded-xl md:rounded-[1.25rem] text-[9px] md:text-[10px] font-black tracking-[0.15em] transition-all flex items-center justify-center md:justify-start gap-3 whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'bg-white text-indigo-600 shadow-md ring-1 ring-slate-100' 
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                <span className="text-sm md:text-lg">{tab.icon}</span>
                <span className="italic">{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="mt-auto hidden md:block border-t border-slate-200 pt-8">
            <button onClick={onLogout} className="flex items-center gap-3 text-rose-500 font-black text-[10px] uppercase tracking-widest hover:translate-x-1 transition-all">
               <span className="text-lg">🚪</span> DECONECTARE CONT
            </button>
          </div>
        </div>

        <div className="flex-grow p-6 sm:p-10 lg:p-16 relative overflow-y-auto custom-scrollbar bg-white">
          <button onClick={onClose} className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center rounded-2xl bg-slate-50 text-slate-400 hover:text-rose-500 transition-all text-3xl z-30 shadow-sm">×</button>

          {showSuccess && (
            <div className="mb-10 p-5 bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-3xl text-[11px] font-black animate-in slide-in-from-top-6 flex items-center gap-4">
              <span className="text-xl">✨</span> {showSuccess}
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-400">
              <div className="mb-12">
                <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tighter italic">Identitate Digitală</h2>
                <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest mt-2 italic">Securitatea profilului LexSmart</p>
              </div>
              <form onSubmit={handleProfileSave} className="space-y-8">
                <div className="space-y-6">
                  <div className="p-6 md:p-8 bg-slate-50/50 rounded-3xl border border-slate-100 transition-all focus-within:border-indigo-200">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 italic">Nume Afișat</label>
                    <input type="text" title="Nume Afișat" aria-label="Nume Afișat" placeholder="Numele tău" value={editedUser.name} onChange={(e) => setEditedUser({...editedUser, name: e.target.value})} className="w-full bg-transparent text-xl md:text-2xl font-black text-slate-900 outline-none placeholder:text-slate-200 italic" />
                  </div>
                  <div className="p-6 md:p-8 bg-slate-50/50 rounded-3xl border border-slate-100 transition-all focus-within:border-indigo-200">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 italic">Email Profesional</label>
                    <input type="email" title="Email Profesional" aria-label="Email Profesional" placeholder="email@exemplu.com" value={editedUser.email} onChange={(e) => setEditedUser({...editedUser, email: e.target.value})} className="w-full bg-transparent text-xl md:text-2xl font-black text-slate-900 outline-none italic" />
                  </div>
                </div>
                <button type="submit" disabled={isSaving} className="w-full py-6 bg-indigo-600 text-white rounded-3xl font-black text-xs uppercase tracking-[0.25em] shadow-2xl shadow-indigo-100 hover:bg-indigo-700 active:scale-[0.98] transition-all">
                  {isSaving ? "SE SALVEAZĂ..." : "SALVARE MODIFICĂRI"}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-400 space-y-8">
              <div className="mb-12">
                <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tighter italic">Setări Cont</h2>
                <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest mt-2 italic">Securitate și preferințe</p>
              </div>

              {settingsError && (
                <div className="p-5 bg-rose-50 border border-rose-200 text-rose-600 rounded-3xl text-[11px] font-black flex items-center gap-4">
                  <span className="text-xl">⚠️</span> {settingsError}
                </div>
              )}

              {/* SCHIMBĂ EMAIL */}
              <div className="p-8 bg-slate-50 rounded-[2.5rem] border border-slate-100 space-y-6">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">📧</span>
                  <div>
                    <h3 className="text-xl font-black text-slate-900 italic">Schimbă Email</h3>
                    <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-1">Email curent: {user.email}</p>
                  </div>
                </div>
                <input 
                  type="email" 
                  title="Email Nou"
                  aria-label="Email Nou"
                  placeholder="Email nou"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="w-full p-5 bg-white border border-slate-200 rounded-2xl outline-none font-bold text-slate-900 focus:border-indigo-500 text-sm placeholder:text-slate-400 transition-all"
                />
                <button
                  onClick={async () => {
                    setSettingsError(null);
                    if (!newEmail || !newEmail.includes('@')) {
                      setSettingsError('Te rugăm să introduci un email valid.');
                      return;
                    }
                    setIsSaving(true);
                    try {
                      const { error } = await supabase.auth.updateUser({ email: newEmail });
                      if (error) throw error;
                      setShowSuccess('Email actualizat! Verifică inbox-ul pentru confirmare.');
                      setNewEmail('');
                      setTimeout(() => setShowSuccess(null), 5000);
                    } catch (err: any) {
                      setSettingsError(err.message || 'Eroare la actualizarea email-ului.');
                    } finally {
                      setIsSaving(false);
                    }
                  }}
                  disabled={isSaving || !newEmail}
                  className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-indigo-700 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'SE ACTUALIZEAZĂ...' : 'ACTUALIZEAZĂ EMAIL'}
                </button>
              </div>

              {/* SCHIMBĂ PAROLĂ */}
              <div className="p-8 bg-slate-50 rounded-[2.5rem] border border-slate-100 space-y-6">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🔒</span>
                  <div>
                    <h3 className="text-xl font-black text-slate-900 italic">Schimbă Parola</h3>
                    <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-1">Minim 6 caractere</p>
                  </div>
                </div>
                <input 
                  type="password" 
                  title="Parola curentă"
                  aria-label="Parola curentă"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Parola curentă"
                  className="w-full p-5 bg-white border border-slate-200 rounded-2xl outline-none font-bold text-slate-900 focus:border-indigo-500 text-sm placeholder:text-slate-400 transition-all"
                />
                <input 
                  type="password" 
                  title="Parola nouă"
                  aria-label="Parola nouă"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Parola nouă"
                  className="w-full p-5 bg-white border border-slate-200 rounded-2xl outline-none font-bold text-slate-900 focus:border-indigo-500 text-sm placeholder:text-slate-400 transition-all"
                />
                <input 
                  type="password" 
                  title="Confirmă parola"
                  aria-label="Confirmă parola"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirmă parola nouă"
                  className="w-full p-5 bg-white border border-slate-200 rounded-2xl outline-none font-bold text-slate-900 focus:border-indigo-500 text-sm placeholder:text-slate-400 transition-all"
                />
                <button
                  onClick={async () => {
                    setSettingsError(null);
                    if (!newPassword || newPassword.length < 6) {
                      setSettingsError('Parola trebuie să aibă minim 6 caractere.');
                      return;
                    }
                    if (newPassword !== confirmPassword) {
                      setSettingsError('Parolele nu se potrivesc.');
                      return;
                    }
                    setIsSaving(true);
                    try {
                      const { error } = await supabase.auth.updateUser({ password: newPassword });
                      if (error) throw error;
                      setShowSuccess('Parolă actualizată cu succes!');
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setTimeout(() => setShowSuccess(null), 3000);
                    } catch (err: any) {
                      setSettingsError(err.message || 'Eroare la actualizarea parolei.');
                    } finally {
                      setIsSaving(false);
                    }
                  }}
                  disabled={isSaving || !newPassword || !confirmPassword}
                  className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-indigo-700 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'SE ACTUALIZEAZĂ...' : 'ACTUALIZEAZĂ PAROLĂ'}
                </button>
              </div>

              {/* INFO PROFIL */}
              <div className="p-8 bg-slate-50 rounded-[2.5rem] border border-slate-100 space-y-6">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">👤</span>
                  <div>
                    <h3 className="text-xl font-black text-slate-900 italic">Informații Profil</h3>
                    <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-1">Nume și barou</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Nume Complet</label>
                    <input 
                      type="text" 
                      title="Nume Complet"
                      aria-label="Nume Complet"
                      placeholder="Ex: Popescu Ion"
                      value={editedUser.name}
                      onChange={(e) => setEditedUser({...editedUser, name: e.target.value})}
                      className="w-full p-5 bg-white border border-slate-200 rounded-2xl outline-none font-bold text-slate-900 focus:border-indigo-500 text-sm transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Barou</label>
                    <input 
                      type="text" 
                      title="Barou"
                      aria-label="Barou"
                      placeholder="Ex: București"
                      value={editedUser.bar}
                      onChange={(e) => setEditedUser({...editedUser, bar: e.target.value})}
                      className="w-full p-5 bg-white border border-slate-200 rounded-2xl outline-none font-bold text-slate-900 focus:border-indigo-500 text-sm transition-all"
                    />
                  </div>
                </div>
                <button
                  onClick={handleProfileSave}
                  disabled={isSaving}
                  className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-indigo-700 active:scale-[0.98] transition-all disabled:opacity-50"
                >
                  {isSaving ? 'SE SALVEAZĂ...' : 'SALVEAZĂ PROFIL'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'support' && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-400 space-y-8">
              <div className="mb-12">
                <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tighter italic">Suport Tehnic</h2>
                <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest mt-2 italic">Contactează echipa LexSmart</p>
              </div>

              {settingsError && (
                <div className="p-5 bg-rose-50 border border-rose-200 text-rose-600 rounded-3xl text-[11px] font-black flex items-center gap-4">
                  <span className="text-xl">⚠️</span> {settingsError}
                </div>
              )}

              <form onSubmit={handleSupportSubmit} className="space-y-6">
                {/* Subject Dropdown */}
                <div className="p-8 bg-slate-50 rounded-[2.5rem] border border-slate-100 space-y-4">
                  <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 italic">Tipul Problemei</label>
                  <select
                    title="Tipul Problemei"
                    aria-label="Tipul Problemei"
                    value={supportSubject}
                    onChange={(e) => setSupportSubject(e.target.value)}
                    className="w-full p-5 bg-white border border-slate-200 rounded-2xl outline-none font-bold text-slate-900 focus:border-indigo-500 text-sm transition-all"
                    required
                  >
                    <option value="">Selectează...</option>
                    <option value="bug">🐛 Raportare Bug</option>
                    <option value="feature">💡 Cerere Funcționalitate</option>
                    <option value="help">❓ Întrebare / Ajutor</option>
                    <option value="other">� Altceva</option>
                  </select>
                </div>

                {/* Message Textarea */}
                <div className="p-8 bg-slate-50 rounded-[2.5rem] border border-slate-100 space-y-4">
                  <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 italic">Mesajul Tău</label>
                  <textarea
                    value={supportMessage}
                    onChange={(e) => setSupportMessage(e.target.value)}
                    rows={8}
                    placeholder="Descrie problema sau întrebarea ta în detaliu..."
                    className="w-full p-5 bg-white border border-slate-200 rounded-2xl outline-none font-medium text-slate-900 focus:border-indigo-500 text-sm placeholder:text-slate-400 transition-all resize-none"
                    required
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={supportSubmitting || !supportSubject || !supportMessage}
                  className="w-full py-6 bg-indigo-600 text-white rounded-3xl font-black text-xs uppercase tracking-[0.25em] shadow-2xl shadow-indigo-100 hover:bg-indigo-700 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {supportSubmitting ? 'SE TRIMITE...' : 'TRIMITE MESAJ 📨'}
                </button>
              </form>

              {/* Info Box */}
              <div className="p-6 bg-blue-50 border border-blue-100 rounded-3xl">
                <div className="flex items-start gap-4">
                  <span className="text-2xl">ℹ️</span>
                  <div>
                    <p className="text-sm font-bold text-blue-900 mb-1 italic">Timp de răspuns</p>
                    <p className="text-xs text-blue-700">Răspundem în maximum 24 de ore în zilele lucrătoare.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;
